/**
 * AI Council — orchestrator (v158 decomposition).
 *
 * Originally a 466-line monolith handling persona selection, moderator,
 * WebSocket streaming, live transcript, result render, and history.
 * Now a thin orchestrator (~80 lines) that wires sub-components:
 *
 *   • <PersonaPicker />        — tier-grouped persona selection
 *   • <ModeratorAndQuestion /> — moderator dropdown + question textarea + Convene
 *   • <LiveTranscript />       — live WS transcript (during stream)
 *   • <CouncilResult />        — synthesised decision (after stream)
 *   • <CouncilHistory />       — recent sessions list
 *   • useCouncilStream         — convene + WS lifecycle hook
 */
import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import PersonaPicker from './ai-council/PersonaPicker';
import ModeratorAndQuestion from './ai-council/ModeratorAndQuestion';
import LiveTranscript from './ai-council/LiveTranscript';
import CouncilResult from './ai-council/CouncilResult';
import CouncilHistory from './ai-council/CouncilHistory';
import useCouncilStream from './ai-council/useCouncilStream';
import { COUNCIL_HARD_CAP } from './ai-council/_constants';

export default function AICouncil() {
  const [personas, setPersonas] = useState([]);
  const [selected, setSelected] = useState([]);
  const [moderator, setModerator] = useState('yusuf');
  const [question, setQuestion] = useState('');

  const { liveAnswers, liveStage, running, result, history, convene } = useCouncilStream();

  useEffect(() => {
    api.get('/ai/employees/').then(({ data }) => setPersonas(data));
  }, []);

  const togglePersona = (id) => {
    setSelected((cur) => {
      if (cur.includes(id)) return cur.filter((p) => p !== id);
      if (cur.length >= COUNCIL_HARD_CAP) {
        toast.error(`Maximum ${COUNCIL_HARD_CAP} council members.`);
        return cur;
      }
      return [...cur, id];
    });
  };

  const selectAll = () => setSelected(personas.map((p) => p.id).slice(0, COUNCIL_HARD_CAP));
  const clearAll = () => setSelected([]);
  const selectTier = (_tier, tierIds) => {
    setSelected((cur) => {
      const merged = Array.from(new Set([...cur, ...tierIds]));
      if (merged.length > COUNCIL_HARD_CAP) {
        toast.error(`Capped at ${COUNCIL_HARD_CAP} — trimmed.`);
        return merged.slice(0, COUNCIL_HARD_CAP);
      }
      return merged;
    });
  };

  const onConvene = () => {
    if (selected.length < 2) return toast.error('Pick at least 2 council members.');
    if (!question.trim()) return toast.error('Ask a question first.');
    convene({ question, personas: selected, moderator });
  };

  const canConvene = selected.length >= 2 && question.trim().length > 0;
  const showLive = running && liveAnswers.length > 0 && !result?._saved;

  return (
    <div className="ve-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow flex items-center gap-2">
            <Sparkles size={14} className="text-[#8B3FC5]"/>
            AI Council
          </div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            Convene your specialists. <span className="ve-gold-text">One decision, many minds.</span>
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Pick 2–{COUNCIL_HARD_CAP} AI personas to debate a question. The full team is welcome — leadership, specialists, industry experts, mentors, security and the concierge. Each member weighs in independently and in parallel; the moderator synthesises a single decision with dissents called out. Every session is audited.
          </p>
        </div>
      </div>

      <PersonaPicker
        personas={personas}
        selected={selected}
        onToggle={togglePersona}
        onSelectAll={selectAll}
        onClear={clearAll}
        onSelectTier={selectTier}
      />

      <ModeratorAndQuestion
        personas={personas}
        moderator={moderator}
        onModeratorChange={setModerator}
        question={question}
        onQuestionChange={setQuestion}
        onConvene={onConvene}
        running={running}
        canConvene={canConvene}
      />

      {showLive && (
        <LiveTranscript
          liveStage={liveStage}
          liveAnswers={liveAnswers}
          personas={personas}
          moderator={moderator}
        />
      )}

      <CouncilResult result={result} personas={personas} />
      <CouncilHistory history={history} personas={personas} />
    </div>
  );
}
